import React from 'react'
import SportManager from '../managers/sport'
import SubscriptionManager from '../connection/subscription'

import { isEmpty } from 'lodash'
import {
  logger,
  removeFromArrayById,
  updateInArrayById,
  verifyCountersBySportServices
} from '../utils'

import {
  SPORTSBOOK_SPORT_REMOVE,
  SPORT_STATE_UPDATE,
  SPORT_CATEGORY_REMOVE,
  SPORT_CATEGORY_ADD,
  SPORT_CATEGORY_UPDATE
} from '../actions'

import { useApplicationState } from './root'
import { useSportsbookState } from './sportsbook'
import { useSettings } from './settings'

import { SPORT_SERVICE } from '../constants'
import { SportsbookError } from '../error'

export const SportContext = React.createContext()

export function SportProvider (props) {
  const {
    sport,
    children
  } = props

  const [
    sportState
  ] = createSportState(sport)

  return (
    <SportContext.Provider value={sportState}>
      {children}
    </SportContext.Provider>
  )
}

export const sportInitialState = {
  categories: [],
  initialized: false,
  loading: true,
  error: null
}

export function useSportState () {
  return React.useContext(SportContext) || sportInitialState
}

function sportReducer (state, action) {
  switch (action.type) {
    case SPORT_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }
    case SPORT_CATEGORY_UPDATE:
      return {
        ...state,
        categories: updateInArrayById(
          state.categories,
          action.payload
        )
      }
    case SPORT_CATEGORY_ADD:
      return {
        ...state,
        categories: updateInArrayById(
          state.categories,
          action.payload
        ).sort((a, b) =>
          (a.position - b.position)
        )
      }
    case SPORT_CATEGORY_REMOVE:
      return {
        ...state,
        categories: removeFromArrayById(
          state.categories,
          action.payload.id
        )
      }
    default:
      return state
  }
}

const createSportInitialState = (sport = {}) => {
  return {
    ...sport,
    ...sportInitialState
  }
}

export const createSportState = initialSportState => {
  const [SportState, SportDispatcher] = React.useReducer(
    sportReducer,
    initialSportState,
    createSportInitialState
  )

  const [sportsbookState] = useSportsbookState()
  const {
    id,
    sportService,
    sportServices,
    fromDate,
    toDate
  } = sportsbookState

  const { connection } = useApplicationState()
  const { lang } = useSettings()

  /*
    Sport categories initialization
  */
  React.useEffect(
    () => {
      if (!SportState.id) { return }

      SportDispatcher({
        type: SPORT_STATE_UPDATE,
        payload: {
          loading: true,
          // initialized: false,
          error: null
        }
      })

      SportManager.findCategories({
        filter: {
          sportService,
          sportServices,
          sportId: SportState.id,
          fromDate,
          toDate
        },
        // Temp solution for resolving all sport categories
        paging: {
          currentPage: 0,
          itemsPerPage: 300
        }
      })
        .then(response => {
          if (isEmpty(response.data)) {
            throw new SportsbookError('No categories found', response)
          }

          SportDispatcher({
            type: SPORT_STATE_UPDATE,
            payload: {
              categories: response.data,
              loading: false,
              initialized: true
            }
          })
        })
        .catch(error => {
          logger.debug(
            `[sport:${SportState.id}]`,
            'unable resolve sport categories: ' + error
          )

          SportDispatcher({
            type: SPORT_STATE_UPDATE,
            payload: {
              loading: false,
              initialized: true,
              error
            }
          })
        })
    },
    [connection.id, SportState.id, lang, fromDate, toDate]
  )

  /*
    Sport dispatcher registration
    Sport categories count subscriptions
  */
  React.useEffect(
    () => {
      if (!SportState.id) { return }
      // Subscribing for categories in sport scope
      const sportSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.CATEGORY_EVENT_COUNT,
          key: SportState.id
        }
      ]

      SubscriptionManager.notifierSubscribe(sportSubscriptionTopics)

      const sportUpdateHandler = action => (
        SportDispatcher(action)
      )

      // Register dispatcher for categories updates
      SportManager.dataBroadcaster.subscribeToSportUpdates(
        SportState.id,
        sportUpdateHandler
      )

      return () => {
        SubscriptionManager.notifierUnsubscribe(sportSubscriptionTopics)

        SportManager.dataBroadcaster.unsubscribeFromSportUpdates(
          SportState.id,
          sportUpdateHandler
        )
      }
    },
    [SportState.id]
  )

  // Sport self destruction
  React.useEffect(
    () => {
      if (SportState.initialized && isEmpty(SportState.categories)) {
        logger.debug(
          `[sport:${SportState.id}]`,
          `self destructing initialized sport in sportsbook#${id} state`
        )

        //  Removing sport from sportsbook state
        SportManager.sportsbookBroadcaster.emit(
          id,
          {
            type: SPORTSBOOK_SPORT_REMOVE,
            payload: {
              id: SportState.id
            }
          }
        )
      }
    },
    [
      SportState.categories,
      SportState.initialized
    ]
  )

  /*
    Handling sport categories event counter subscriptions
    Inside sport scope updating/adding/removing categories
  */
  React.useEffect(
    () => {
      if (!SportState.id) { return }

      // Creating handler for counters for categories inside sport scope
      const handleSportCounterUpdate = data => {
        const {
          id, // category id
          prematchCount,
          liveCount
        } = data

        const shouldExist = verifyCountersBySportServices(
          sportServices || sportService,
          {
            [SPORT_SERVICE.PREMATCH]: prematchCount,
            [SPORT_SERVICE.LIVE]: liveCount
          }
        )

        if (!shouldExist) {
          SportDispatcher({
            type: SPORT_CATEGORY_REMOVE,
            payload: {
              id
            }
          })

          return
        }

        const isCategoryExist = SportState.categories.find(
          category => category.id === id
        )

        if (isCategoryExist) {
          SportDispatcher({
            type: SPORT_CATEGORY_UPDATE,
            payload: {
              id,
              prematchCount,
              liveCount
            }
          })

          return
        }

        SportManager.findCategories({
          filter: {
            sportService,
            sportServices,
            categoryIds: [
              id
            ],
            sportId: SportState.id
          }
        })
          .then(res => {
            if (isEmpty(res.data)) { return }
            const [category] = res.data

            if (category) {
              SportDispatcher({
                type: SPORT_CATEGORY_ADD,
                payload: category
              })
            }
          })
      }

      SportManager.counterBroadcaster.subscribeToSportUpdates(
        SportState.id,
        handleSportCounterUpdate
      )

      return () => {
        SportManager.counterBroadcaster.unsubscribeFromSportUpdates(
          SportState.id,
          handleSportCounterUpdate
        )
      }
    },
    [
      SportState.id,
      SportState.categories
    ]
  )

  return [
    SportState,
    SportDispatcher
  ]
}

/*
  const useSportsCounterSubcription = (handleSportCounterUpdate) => {
    return React.useEffect(
      () => {
        async function handleSportEventsCount ({ data }) {
          // const {
          //   id,
          //   liveCount,
          //   prematchCount
          // } = data
          return handleSportCounterUpdate(data)
        }

        Bus.on(Bus.events.sport.sportEventsCount, handleSportEventsCount)

        return () => {
          Bus.off(Bus.events.sport.sportEventsCount, handleSportEventsCount)
        }
      },
      []
    )
  }
*/
